/**
 * B397 leg (1) — the booking page's derivations, pure (75:2).
 *
 * Every sentence on the booking page comes from a READ or is the honest
 * empty face (§10): the hero headline is derived from the catalog because
 * no channel read carries one (noted on B397's line — the class-B channel
 * amendment priced on B355's line would replace the derivation); the card
 * meta carries route and kind because `presentation.journey` and
 * `scheduleIds` carry them; the accompaniment sentence exists only where
 * the catalog actually carries a requires-constraint. Nothing here invents
 * a duration, a next-sailing or a tenant fact the platform has not stated.
 */
import type { VocabularyPack } from "@transact/ui";
import type { Money } from "./money.js";

/**
 * B403 #22-extension — the slice of the tenant's vocabulary pack the
 * booking derivations spend: the buyer word for the sellable moment and
 * the plain catalog forms ("bicycle" -> "bike"). ABSENT (no tenantType on
 * the mint yet — the pre-stack-apply wire, or a type-less tenant) keeps
 * the page's standing "departure" register exactly as measured; GENERIC
 * is deliberately NOT the fallback here because flipping the deployed
 * words on a wire absence would regress the measured booking row.
 */
export type BuyerVocabulary = Pick<VocabularyPack, "departureNoun" | "plainForms" | "cardCta">;
import type { PoolAvailability, ProductSummary, SlotSummary } from "../ui/screens.js";
import { seatsLeft } from "../ui/departures.js";
import { clockWords } from "./when.js";

/** "a" or "an" by the word's own first sound, naively — buyer copy, not grammar science. */
function article(word: string): "a" | "an" {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

/**
 * Kind modifiers a verb object keeps (review #1 (1c)): "Day Pass" IS the
 * kind, "Harbor Ferry Crossing" is a compound whose kind is "Crossing".
 * Closed set, buyer copy, not grammar science — the same register as
 * `article()` above. AT always gets the full name (cardAction's ariaLabel).
 */
const KIND_MODIFIERS: ReadonlySet<string> = new Set([
  "day",
  "night",
  "morning",
  "evening",
  "weekend",
  "season",
  "round",
  "family",
  "group",
  "annual",
  "monthly",
]);

/**
 * The verb OBJECT — the product's KIND (review #1 (1c): "the verb takes the
 * product KIND, not the full name; the title is already the line above").
 * Two steps, both mechanical: the last dash-separated segment first
 * ("Island Ferry — Round Trip" → "Round Trip"; a dash inside a caption is
 * B172's dash-sentence shape, which the caption net rightly refuses), then
 * the segment's own tail — the last word, keeping a preceding KIND modifier
 * ("Day Pass" stays whole; "Harbor Ferry Crossing" → "Crossing";
 * "10-Ride Pass" → "Pass").
 */
export function verbObject(name: string): string {
  const segments = name.split(/\s+[—–-]\s+/).filter((s) => s.length > 0);
  const segment = segments.length === 0 ? name : (segments[segments.length - 1] as string);
  const words = segment.split(/\s+/).filter((w) => w.length > 0);
  if (words.length <= 1) return segment;
  const penult = (words[words.length - 2] as string).toLowerCase();
  if (KIND_MODIFIERS.has(penult)) return words.slice(-2).join(" ");
  return words[words.length - 1] as string;
}

/** True when the product rides departures — the fact `scheduleIds`/`journey` carry. */
export function ridesDepartures(card: Pick<ProductSummary, "scheduleIds" | "presentation">): boolean {
  return card.scheduleIds.length > 0 || card.presentation?.journey !== undefined;
}

export interface CardAction {
  /** The visible caption ("Book a Round Trip") — the sheet's CSS shouts it. */
  readonly label: string;
  /** The full product's name for AT when the visible object shortened it; absent when they match. */
  readonly ariaLabel?: string;
}

/**
 * The card's ONE action, verbed by what the product IS: departures are
 * BOOKED, the rest is BOUGHT — unless the tenant's pack names the action
 * outright (B614: SHOW says "Get tickets"; a headliner is not a thing one
 * "books a" — "Book a Barbosa" was measured on the Phoenix demo, 2026-10-01).
 */
export function cardAction(card: Pick<ProductSummary, "name" | "scheduleIds" | "presentation">, vocab?: BuyerVocabulary): CardAction {
  if (vocab?.cardCta !== undefined) return { label: vocab.cardCta, ariaLabel: `${vocab.cardCta} — ${card.name}` };
  const verb = ridesDepartures(card) ? "Book" : "Buy";
  const object = verbObject(card.name);
  const label = `${verb} ${article(object)} ${object}`;
  if (object === card.name) return { label };
  return { label, ariaLabel: `${verb} ${article(card.name)} ${card.name}` };
}

export type PriceFace =
  | { readonly kind: "from" | "exact"; readonly amount: Money }
  | { readonly kind: "quote" };

/**
 * 75:2 zone 2: `from $12.00` when per-type prices differ (or a B170 route
 * fact says they do), the bare amount when every type costs the same,
 * `by quote` when no read carries a price at all.
 */
export function priceFace(
  card: ProductSummary,
  facts?: ReadonlyMap<string, { readonly amount: Money; readonly from: boolean }>,
): PriceFace {
  const priced: { readonly amount: Money; readonly from: boolean; readonly fare: boolean }[] = [];
  for (const v of card.variants) {
    const fare = v.constraints?.requiresVariantId === undefined;
    const fact = facts?.get(v.variantId);
    if (fact !== undefined) priced.push({ ...fact, fare });
    else if (v.displayPrice !== undefined) priced.push({ amount: v.displayPrice, from: false, fare });
  }
  if (priced.length === 0) return { kind: "quote" };
  /* B403 #26 (h), the recorded class-B: the headline prices a FARE — a
     type that requires another type (Child, Bicycle) rides someone else's
     booking and is not what a crossing costs, so 75:2 says "from $12.00",
     never the bicycle's $4. A catalog whose every type requires another
     falls back to the honest overall minimum rather than silence. */
  const fares = priced.filter((p) => p.fare);
  const pool = fares.length > 0 ? fares : priced;
  let min = pool[0] as { readonly amount: Money; readonly from: boolean };
  for (const p of pool) if (p.amount.amount < min.amount.amount) min = p;
  const distinct = new Set(priced.map((p) => `${p.amount.currency}:${p.amount.amount}`));
  const from = priced.some((p) => p.from) || distinct.size > 1;
  return { kind: from ? "from" : "exact", amount: min.amount };
}

/**
 * 75:2 zone 1's meta line — ONLY facts a read carries, dot-joined:
 * the journey's route and the kind. No read carries a duration, so no
 * card states one (§10 — the honest face is the absence).
 *
 * B403 #39-b (2026-08-28, the design side's "vocab call", answered): the
 * "timed departure" clause STAYS the operator register — it is a LABEL
 * register the 08-26 ruling keeps on the operator word, and 75:2 itself
 * draws "timed departure" in dev-ferry's frame (gated in
 * bookingCopy.test.tsx). Reversal: thread vocab through here, one line
 * per caller.
 */
export function cardMeta(card: Pick<ProductSummary, "scheduleIds" | "presentation">, vocab?: BuyerVocabulary): string | undefined {
  const parts: string[] = [];
  const journey = card.presentation?.journey;
  if (journey !== undefined) parts.push(`${journey.from.name} → ${journey.to.name}`);
  /* B614: a pack that names the card's action (SHOW) says when on the
     poster — "timed departure" under a comedian's name is the ferry's
     register leaking (measured on the Phoenix demo, 2026-10-01). */
  if (ridesDepartures(card) && vocab?.cardCta === undefined) parts.push("timed departure");
  return parts.length === 0 ? undefined : parts.join(" · ");
}

/**
 * B403 #39-b — the return-leg action verb sentence, in the tenant's own
 * word ("Choose return sailing"); the standing register without a pack.
 * A buyer SENTENCE per the #22-extension, unlike cardMeta's label above —
 * and App.tsx previously hardcoded the FERRY word, which was wrong for
 * every other tenant (walled: the consumption test refuses the literal).
 */
export function returnPickLabel(vocab?: BuyerVocabulary): string {
  return `Choose return ${vocab?.departureNoun.singular ?? "departure"}`; // vocab-ok: the documented pre-pack fallback — the mint's tenantType flips it (B126 seam)
}

export interface ChipFact {
  readonly dot: "neutral";
  readonly label: string;
}

/**
 * The status chip's NEUTRAL note ("Needs an adult ticket"), read from the
 * §4.2 requires-constraint. The GREEN live fact the frame also draws
 * ("Next sailing 10:30 · 36 seats left") rides a departures read the
 * catalog step has not made — until it does, the honest face is no chip,
 * never a guessed one.
 */
export function chipFact(card: ProductSummary): ChipFact | undefined {
  for (const v of card.variants) {
    const requiredId = v.constraints?.requiresVariantId;
    if (requiredId === undefined) continue;
    const required = card.variants.find((r) => r.variantId === requiredId);
    if (required === undefined) continue;
    return { dot: "neutral", label: `Needs ${article(required.name)} ${required.name.toLowerCase()} ticket` };
  }
  return undefined;
}

/**
 * Naive buyer-copy plural — letters only ("Group 10+" stays itself), a
 * double-s takes -es ("day pass" → "day passes"), and a word already
 * ending in one s is left alone rather than guessed at ("tickets").
 */
const IRREGULAR_PLURALS: Readonly<Record<string, string>> = {
  child: "children",
  person: "people",
};

/** Buyer-copy plural, irregulars included — shared with the picks line. */
export function plural(word: string): string {
  const irregular = IRREGULAR_PLURALS[word.toLowerCase()];
  if (irregular !== undefined) return irregular;
  if (!/[a-z]$/i.test(word)) return word;
  if (/ss$/i.test(word)) return `${word}es`;
  return /s$/i.test(word) ? word : `${word}s`;
}

/** "Child (2-12)" → "child": the buyer sentence drops the qualifier the card already states. */
export function plainName(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/u, "").trim().toLowerCase();
}

/**
 * The hero H1 — the tenant's catalog summarized ("Crossings, day passes
 * and bikes"-shaped), derived from the products' own kind segments until
 * a channel read carries a hand-written headline. Empty catalog: the
 * plain truthful "Tickets".
 */
export function catalogSummary(products: readonly Pick<ProductSummary, "name">[], vocab?: BuyerVocabulary): string {
  const seen = new Set<string>();
  const kinds: string[] = [];
  for (const p of products) {
    const base = verbObject(p.name).toLowerCase();
    const kind = plural(vocab?.plainForms[base] ?? base);
    if (seen.has(kind)) continue;
    seen.add(kind);
    kinds.push(kind);
  }
  if (kinds.length === 0) return "Tickets";
  const head = kinds.slice(0, 3);
  const tail = kinds.length > 3 ? " and more" : "";
  const joined =
    head.length === 1
      ? (head[0] as string)
      : `${head.slice(0, -1).join(", ")} and ${head[head.length - 1] as string}`;
  const sentence = kinds.length > 3 ? `${head.join(", ")}${tail}` : joined;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * The hero sub's SECOND sentence — exists only where the catalog carries
 * an accompaniment rule (75:2: "Second sentence exists only where the
 * catalog carries such a rule"), and names the tenant's own ticket types
 * rather than the frame's demo words.
 */
export function accompanimentNote(products: readonly ProductSummary[], vocab?: BuyerVocabulary): string | undefined {
  const requiring: string[] = [];
  const required = new Set<string>();
  const seen = new Set<string>();
  let ridesTimed = false;
  for (const p of products) {
    for (const v of p.variants) {
      const reqId = v.constraints?.requiresVariantId;
      if (reqId === undefined) continue;
      const req = p.variants.find((r) => r.variantId === reqId);
      if (req === undefined) continue;
      /* B403 #26 (b) — 75:2's plain register: lowercase, pluralised, the
         card's parenthetical qualifier dropped ("Child (2-12)" → children). */
      /* B403 #22-extension: the pack's plain form first ("bicycle" -> "bike"). */
      const base = plainName(v.name);
      const key = plural(vocab?.plainForms[base] ?? base);
      if (!seen.has(key)) {
        seen.add(key);
        requiring.push(key);
      }
      required.add(plainName(req.name));
      if (ridesDepartures(p)) ridesTimed = true;
    }
  }
  if (requiring.length === 0 || required.size === 0) return undefined;
  const req = [...required][0] as string;
  const who =
    requiring.length === 1
      ? (requiring[0] as string)
      : `${requiring.slice(0, -1).join(", ")} and ${requiring[requiring.length - 1] as string}`;
  const whoSentence = who.charAt(0).toUpperCase() + who.slice(1);
  /* B126: no industry noun in .ts copy beyond the "departure" register the
     page already speaks (cardMeta's "timed departure") — the frame's "on
     the same sailing" is this clause in dev-ferry's tenant word, the
     B410-exempt slot. Untimed catalogs keep the ticket wording. */
  /* B403 #22-extension: the tenant's own buyer word where the mint carried
     a type ("on the same sailing"); the standing register otherwise. */
  const moment = vocab?.departureNoun.singular ?? "departure"; // vocab-ok: the documented pre-pack fallback — the mint's tenantType flips it (B126 seam closed 2026-08-26)
  return ridesTimed
    ? `${whoSentence} travel with ${article(req)} ${req} on the same ${moment}.`
    : `${whoSentence} travel with ${article(req)} ${req} ticket.`;
}

/**
 * B403 #26 (e) — the card's GREEN live fact, off real reads alone:
 * "Next departure 10:30 · 36 seats left". The time is the next upcoming
 * ON_SALE slot's, in the storefront register (24h); the seats clause
 * renders only when an availability read answered for THAT slot — the
 * honest face of an unread count is the shorter line, and the honest
 * face of no upcoming departure is no fact at all (§10).
 */
export function nextDepartureFact(
  slots: readonly Pick<SlotSummary, "slotId" | "start" | "status">[],
  nextAvailability: readonly PoolAvailability[],
  now: number,
  opts: { readonly locale?: string; readonly timeZone?: string; readonly vocab?: BuyerVocabulary } = {},
): string | undefined {
  const next = [...slots]
    .filter((s) => s.status === "ON_SALE" && s.start >= now)
    .sort((a, b) => a.start - b.start)[0];
  if (next === undefined) return undefined;
  const left = seatsLeft(nextAvailability);
  const moment = opts.vocab?.departureNoun.singular ?? "departure"; // vocab-ok: the documented pre-pack fallback — the mint's tenantType flips it to 75:2's "Next sailing"
  const head = `Next ${moment} ${clockWords(next.start, opts)}`;
  return left === undefined ? head : `${head} · ${left} ${left === 1 ? "seat" : "seats"} left`;
}

/** 75:2's ONE-primary rule: the flagship is the catalog's FIRST product — deterministic, catalog-ordered. */
export function flagshipProductId(products: readonly Pick<ProductSummary, "productId">[]): string | undefined {
  return products.length === 0 ? undefined : (products[0] as Pick<ProductSummary, "productId">).productId;
}
